import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Switch } from '@multaqa/ui/components/ui/switch'
import { useState } from 'react'
import { localizedText } from '@/lib/localized-text'
import type { DepartmentResource, DepartmentWriteInput } from './api'
import { useCreateDepartment, useUpdateDepartment } from './api'
import { localizedValues } from './company-formatters'

export function CompanyDepartmentDialog({
  department,
  list,
  groupId,
  companyId,
  open,
  onOpenChange,
}: {
  department: DepartmentResource | null
  list: DepartmentResource[]
  groupId: string
  companyId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const create = useCreateDepartment()
  const update = useUpdateDepartment()
  const [nameEn, setNameEn] = useState(department?.name.en ?? '')
  const [nameAr, setNameAr] = useState(department?.name.ar ?? '')
  const [code, setCode] = useState(department?.code ?? '')
  const [parentId, setParentId] = useState(department?.parent_id ?? '')
  const [active, setActive] = useState(department?.is_active ?? true)
  const [message, setMessage] = useState<string | null>(null)
  const mutation = department ? update : create
  const candidates = list.filter(
    (item) => item.id !== department?.id && !isDescendant(item.id, department?.id ?? '', list),
  )

  function save() {
    const name = localizedValues(nameEn, nameAr)
    if (Object.keys(name).length === 0) return setMessage(t('companies.departments.nameRequired'))
    if (!code.trim()) return setMessage(t('companies.departments.codeRequired'))
    const input: DepartmentWriteInput = {
      name,
      code: code.trim().toUpperCase(),
      parent_id: parentId || null,
      is_active: active,
    }
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        toast.success(t('state.saved'))
      },
      onError: () => setMessage(t('companies.departments.saveFailed')),
    }
    if (department)
      update.mutate({ groupId, companyId, departmentId: department.id, input }, options)
    else create.mutate({ groupId, companyId, input }, options)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('action.cancel')}
        className="max-w-lg"
        data-testid="company-department-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              department ? 'companies.departments.editTitle' : 'companies.departments.createTitle',
            )}
          </DialogTitle>
          <DialogDescription>{t('companies.departments.subtitle')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          noValidate
        >
          <DialogBody className="grid gap-4">
            {message ? (
              <p className="text-small text-danger" role="alert">
                {message}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="company-department-name-en"
                label={t('companies.departments.nameEn')}
                value={nameEn}
                dir="ltr"
                onChange={setNameEn}
              />
              <TextField
                id="company-department-name-ar"
                label={t('companies.departments.nameAr')}
                value={nameAr}
                dir="rtl"
                onChange={setNameAr}
              />
            </div>
            <TextField
              id="company-department-code"
              label={t('companies.departments.code')}
              value={code}
              dir="ltr"
              onChange={(value) => setCode(value.toUpperCase())}
            />
            <div className="grid gap-1.5">
              <Label htmlFor="company-department-parent">{t('companies.departments.parent')}</Label>
              <Select
                value={parentId || '__none__'}
                onValueChange={(value) => setParentId(value === '__none__' ? '' : value)}
              >
                <SelectTrigger id="company-department-parent">
                  <SelectValue placeholder={t('companies.departments.parentNone')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('companies.departments.parentNone')}</SelectItem>
                  {candidates.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {localizedText(item.name, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex min-h-8 items-center justify-between gap-3">
              <Label htmlFor="company-department-active">{t('companies.departments.active')}</Label>
              <Switch id="company-department-active" checked={active} onCheckedChange={setActive} />
            </div>
          </DialogBody>
          <DialogFooter showCloseButton={false}>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
              data-testid="company-department-submit"
            >
              {t(
                department
                  ? 'companies.departments.submitEdit'
                  : 'companies.departments.submitCreate',
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function isDescendant(
  candidateId: string,
  rootId: string,
  list: DepartmentResource[],
): boolean {
  if (!rootId) return false
  const byId = new Map(list.map((item) => [item.id, item]))
  const visited = new Set<string>()
  let parentId = byId.get(candidateId)?.parent_id ?? null
  while (parentId && !visited.has(parentId)) {
    if (parentId === rootId) return true
    visited.add(parentId)
    parentId = byId.get(parentId)?.parent_id ?? null
  }
  return false
}

function TextField({
  id,
  label,
  value,
  dir,
  onChange,
}: {
  id: string
  label: string
  value: string
  dir: 'ltr' | 'rtl'
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} dir={dir} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}
